import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import AuthPageLayout from '../components/layout/AuthPageLayout';
import LoginForm from '../components/auth/LoginForm';

const LOGIN_REASON_MESSAGES = {
  expired: 'Your session has expired. Please sign in again.',
  timeout: 'You were signed out due to inactivity. Please sign in again.',
};

const LoginPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const { showError, showWarning, hideAlert } = useUI();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Surface a friendly notice when arriving here after a session timeout/expiry.
  useEffect(() => {
    const reason = searchParams.get('reason');
    const message = LOGIN_REASON_MESSAGES[reason];
    if (message) {
      showWarning(message);
      // Clear the query param so a refresh doesn't re-trigger the banner.
      const next = new URLSearchParams(searchParams);
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (username, password) => {
    hideAlert();

    // Client-side validation
    const validationErrors = [];
    if (!username.trim()) {
      validationErrors.push('Username is required.');
    }
    if (!password) {
      validationErrors.push('Password is required.');
    }
    if (validationErrors.length > 0) {
      showError(validationErrors.join(' '));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await login(username, password);

      if (response.success) {
        if (response.requiresMfa) {
          navigate(ROUTES.MFA);
        } else {
          // Redirect to React search page
          navigate(ROUTES.SEARCH);
        }
      } else {
        showError(response.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout title="Login" showCookieBanner>
      <LoginForm
        onSubmit={handleLogin}
        isSubmitting={isSubmitting}
      />
    </AuthPageLayout>
  );
};

export default LoginPage;